import { AfterViewInit, ChangeDetectorRef, Component, DoCheck, ElementRef, EnvironmentInjector, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { mongoId, waitForVariable } from '../../../@core/common/global';
import { ApiService } from '../../../@core/service/api.service';
import { SharedDataService } from '../../../@core/service/shared-data.service';
import { BusinessModel, ContactFieldModel, EContactFieldType, EContactType } from '../../../@models';
import { CacheType, CompilationModel } from '../../../@models/erp/compilation';
import { CompilationTypeData, DocConfig, PalletType, DocumentStatusData, PackingStatusData, TypeOfCartonBox, FiscalYearData } from '../../../@models/erp/compilationData';
import { ConditionalExpr } from '@angular/compiler';
import { FinishedProductModel } from '../../../@models/erp/finishedProduct';
import * as saveAs from 'file-saver';
import * as ExcelJS from 'exceljs';
import * as moment from 'moment';
import { ReportsComponent } from '../reports/reports.component';
import { QuillEditorComponent } from 'ngx-quill';
import QuillTableBetter from 'quill-table-better';
import Quill from 'quill';

export interface IWeeklyEmailInfo {
  lastWeekOR: { // sum in EUR
    nrvi: number,
    nrmm: number,
    nrbm: number,
    em: number,
  };
  lastWeekIN: { // sum in EUR
    nrvi: number,
    nrmm: number,
    nrbm: number,
    em: number,
  };
  currentStockLevelEUR: number;
  currentStockLevelUSD: number;
}

abstract class AbstractEditor {
  public data: any;
  public columns: any;
  protected abstract title: string;
  protected abstract items: any;

  orderConfirmations: CompilationModel[] = []
  packingLists: CompilationModel[] = []
  deliveryNotes: CompilationModel[] = []
  invoices: CompilationModel[] = []
  dnTree: { [code: string]: { // Legacy Util delivery notes tree
    oc: CompilationModel
    pl: CompilationModel
    dn: CompilationModel
    in: CompilationModel
  } } = {}

  loading = false;

  showFooter = true;

  constructor(private utilType: string, public api: ApiService, protected toast: NbToastrService) {
  }

  async getItems() {
    if (!this.data) {
      this.data = await this.api.getCompilationData(this.utilType);
      const copy = [];
      if (!this.items.length) {
        copy.push(...this.data)        
      } else {
        for (const item of this.items) {
          copy.push(this.data.find(f => f?.id === item.id) || item) // TODO: remove item?
        }
      }
      this.items = copy;
    }
    this.initItems(this.items);
    return this.items;
  }

  // override
  initItems(items: any) {}

  async saveItems(items = null) {
    console.log('items data', items, this.data);
    await this.api.saveCompilationData(this.utilType, items ? items : this.data).then(() => {
      this.toast.success('Data successfully saved', this.title);
    }).catch(err => {
      console.error(err);
      this.toast.danger('Error saving data', 'Error');
    });
  }

  async get(checklistType: string) {
    return (await this.getItems()).find(f => f.id === checklistType);
  }

  getData = () => this.data;
  setData = (data: any) => this.data = data;
  getColumns = () => this.columns;
}

export class ChecklistUtil extends AbstractEditor {
  protected title = 'Checklist Editor';
  protected items = [
    { id: 'CN', name: 'Credit note (if any)', data: [] },
    { id: 'PC', name: 'Payment confirmation', data: [] },
    { id: 'IN', name: 'Invoice to customer', data: [] },
    { id: 'PL', name: 'Packing List', data: [] },
    { id: 'PD', name: 'Proof of delivery', data: [] },
    { id: 'TC', name: 'Test certificate', data: [] },
    { id: 'PFIN', name: 'Pro Forma Invoice from other branch', data: [] },
    { id: 'OC', name: 'Order confirmation (OC)', data: [] },
    { id: 'PO', name: 'Customer Purchase Order (PO)', data: [] },
    { id: 'OR', name: 'Order (OR)', data: [] },
  ]
}

export class ChangelogUtil extends AbstractEditor {
  protected title = 'Changelog Quicktext Editor';
  protected items = [
    { id: 'GN', name: 'General', data: [] },
    { id: 'BP', name: 'Base Product', data: [] },
    { id: 'FP', name: 'Finished Product', data: [] },
    ...CompilationTypeData.getTypes().map(m => ({
      id: m.code,
      name: m.name,
      data: [],
    })),
  ]
}

export class FileDescriptionUtil extends AbstractEditor {
  protected title = 'File Descriptions';
  protected items = [
    { id: 'relatedDocuments', name: 'Enclosed documents -> related documents', data: [] },
  ]
}

export class TaxHintUtil extends AbstractEditor {
  protected title = 'Tax Hints';
  protected items: string[] = []
}

export class DisplayDatesTextUtil extends AbstractEditor {
  protected title = 'Display Dates Text';
  protected items: string[] = [];
}

export class CurrencyUtil extends AbstractEditor {
  protected title = 'Currency Editor';
  protected items: any = [];
  public columns: any = [
    { id: 'validFrom', name: 'valid from', type: 'date' },
    { id: 'jpy', name: 'JPY ¥', type: 'number' },
    { id: 'usd', name: 'USD $', type: 'number' },
  ]
  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, private store: SharedDataService) {
    super(utilType, api, toast);
  }
  addRow(idx: number) {
    this.data.splice(idx, 0, {
      validFrom: new Date(),
      jpy: '',
      usd: '',
    })
  }
  removeRow(idx: number) {
    this.data.splice(idx, 1);
  }
  getEUR(fromCurrency: string, value: number) {
    if (fromCurrency === 'EUR') return value;
    const entry = this.store.compilationData.currency.find(f => f.validFrom < new Date());
    return value / entry[fromCurrency];
  }
}

export class HsCodeUtil extends AbstractEditor {
  protected title = 'HS Code Editor';
  protected items: any = [];
  public columns: any = [
    { id: 'type', name: 'Type', type: 'number' },
    { id: 'code', name: 'HS Code', type: 'string' },
    { id: 'text', name: 'Description', type: 'string' },
  ]
  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, private store: SharedDataService) {
    super(utilType, api, toast);
  }
  addRow(idx: number) {
    this.data.splice(idx+1, 0, {
      type: 0,
      code: '',
      text: '',
    })
  }
  removeRow(idx: number) {
    this.data.splice(idx, 1);
  }
}

export class FiscalYearUtil extends AbstractEditor {
  protected title = 'Fiscal Year Editor';
  protected items: any = [];
  protected finishedProducts: FinishedProductModel[] = [];

  protected yearOptions = FiscalYearData.getOptions();

  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, private shared: SharedDataService) {
    super(utilType, api, toast);
    this.init();
  }

  async init() {

    await this.shared.loadProducts();
    await this.shared.loadBusinesses();

    const docs = (await this.api.filterCompilations(
      { type: { $in: [CompilationTypeData.get('OC').id, CompilationTypeData.get('PL').id, CompilationTypeData.get('DN').id, CompilationTypeData.get('IN').id] } },
      // { type: CompilationTypeData.get('IN').id },
      '_id code type parent documentStatus issueDate vieDispatchDate recipient fiscalYear lineItems.finishedProduct',
      true)
    )
    .filter(inv => +inv.documentStatus === DocumentStatusData.get('final').id)

    console.log('fiscal docs count',docs);

    docs.forEach((doc, idi) => {
      doc.lineItems.forEach((li, idx) => 
        doc.lineItems[idx].finishedProduct = this.shared.finishedProducts.find(f => mongoId(f) === mongoId(li.finishedProduct))
      )
      docs[idi].recipient = this.shared.businesses.find(f => mongoId(f) === mongoId(doc.recipient));
      if (doc.type === CompilationTypeData.get('IN').id) {
        let tmp: CompilationModel = doc;
        while (tmp && !/^OCNRK/.test(tmp.code)) {
          tmp = docs.find(f => f.code === tmp.parent);
        }
        // console.log('parentDocType',parentDoc?.type);
        if (tmp?.type === CompilationTypeData.get('OC').id) {
          docs[idi].vieDispatchDate = tmp.vieDispatchDate;
          if (doc.code === 'INNRKA230261') console.log('doc', doc.code, tmp.code, doc)
        }
      }
    })

    this.invoices = docs.filter(f => f.type === CompilationTypeData.get('IN').id)
    .sort((a, b) => a.issueDate < b.issueDate ? 1 : (a.issueDate > b.issueDate ? -1 : 0))
    .sort((a, b) => a.vieDispatchDate < b.vieDispatchDate ? 1 : (a.vieDispatchDate > b.vieDispatchDate ? -1 : 0))

    console.log('shared data', this.shared.finishedProducts, this.invoices) //shared.compilationItems, this.invoices);
  }

  fiscalYearChanged(doc: CompilationModel) {
    doc['updated'] = true;
  }
  
}

export class LegacyDataUtil extends AbstractEditor {
  protected title = 'Legacy Data Editor';
  protected items: any = []

  deliveryNotes: any = [];

  earlierDate: Date;

  showFooter = false;

  setStatusInDnTree(code: string, status: number) {
    const promises = [];
    const tree = this.dnTree[code];
    if (tree.oc) promises.push(this.api.updateField(tree.oc.code, 'packingStatus', status));
    if (tree.pl) promises.push(this.api.updateField(tree.pl.code, 'packingStatus', status));
    if (tree.dn) promises.push(this.api.updateField(tree.dn.code, 'packingStatus', status));
    if (tree.in) promises.push(this.api.updateField(tree.in.code, 'packingStatus', status));
    return Promise.all(promises);
  }

  setDnStatus(dn: any, status: number) {
    dn.packingStatus = status;
    return this.setStatusInDnTree(dn.code, status);
  }

  setAllProvedCompleted() {
    const confirmed = window.confirm('Are you sure to set all Documents with Prove of Delivery completed ?')
    if (!confirmed) return;
    this.loading = true;
    const promises = this.deliveryNotes.map(async dn => {
      // console.log('setAllProvedCompleted', dn.invoicePoD, dn);
      if (dn.invoicePoD?.files.length) return await this.setDnStatus(dn, PackingStatusData.get('completed').id);
    })
    console.log('promises length', promises.length)
    Promise.all(promises).then(() => this.loading = false);
  }

  async setAllEarlierCompleted() {
    const confirmed = window.confirm('Are you sure to set all Documents with dispatch date earlier than ' + new Date(this.earlierDate).toISOString().replace(/T.*/, '') + ' completed ?\nThis cannot be undone!')
    if (!confirmed) return;
    this.loading = true;
    const promises = this.deliveryNotes.map(async dn => {
      // console.log('setAllEarlierCompleted', dn.ocDispatch, dn);
      if (new Date(dn.ocDispatch) < new Date(this.earlierDate)) return await this.setDnStatus(dn, PackingStatusData.get('completed').id);
    })
    Promise.all(promises).then(() => this.loading = false);
  }
}

export class BusinessToPUtil extends AbstractEditor {
  protected title = 'Business Terms of Payment Editor';
  protected items: any = []

  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, private store: SharedDataService) {
    super(utilType, api, toast);
    this.store.loadBusinesses();
    this.store.loadCompilationData();
  }

  getField(business: BusinessModel, type: number) {
    const contact = business?.contacts.find(f => f.type === EContactType.General);
    return contact?.fields?.find(f => +f.type === +type)?.value || '';
  }
  setField(business: BusinessModel, type: number, value: string) {
    const contact = business?.contacts.find(f => f.type === EContactType.General);
    const field = contact?.fields?.find(f => +f.type === +type);
    if (field) {
      field.value = value;
    } else {
      const newField = { type: +type, value: value };
      contact?.fields.push(newField);
    }
    this.api.saveBusiness(business)
  }
  override async saveItems(items: any) {
    this.store.reloadBusinesses();
  }
}

export class PaymentTextUtil extends AbstractEditor {
  protected title = 'Invoice Due Dates / Payment Text Editor';
  protected items: any = [
    { id: 'paymentText', name: 'Terms of Payment', data: [{}] },
    { id: 'termsOfPayment', name: 'Period of Payment', data: [{}] },
    { id: 'paymentPlanText', name: 'Payment Schedule', data: [{}] },
  ]

  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, public store: SharedDataService) {
    super(utilType, api, toast);
  }

  reportDateFieldOptions = DocConfig.reportDateFieldOptions;
  // reportDateFieldOptions = [
  //   { id: 'issueDate', name: 'Issue Date' },
  //   ...DocConfig.getArray().filter(f => 
  //     f.value.type === 'reportDate' 
  //     && f.key !== 'maxArrivingVieDate'
  //   ).map(m => ({ id: m.key, name: m.value.label })),
  // ]

  paymentText: any[];
  termsOfPayment: any[];
  paymentPlanText: any[];
  public override initItems(items: any): void {
    this.paymentText = this.items.find(f => f.id === 'paymentText').data?.sort((a, b) => a.text.localeCompare(b.text));
    this.termsOfPayment = this.items.find(f => f.id === 'termsOfPayment').data?.sort((a, b) => !a.days || +a.days < +b.days ? 1 : -1);
    this.paymentPlanText = this.items.find(f => f.id === 'paymentPlanText').data?.sort((a, b) => a.text.localeCompare(b.text));
    items.find(f => f.id === 'paymentText').name = 'Terms of Payment';
    items.find(f => f.id === 'termsOfPayment').name = 'Period of Payment';
    items.find(f => f.id === 'paymentPlanText').name = 'Payment Schedule';
  }

  public override saveItems(items?: any): Promise<void> {
    this.items.find(f => f.id === 'paymentText').data = this.paymentText?.sort((a, b) => a.text.localeCompare(b.text));
    this.items.find(f => f.id === 'termsOfPayment').data = this.termsOfPayment?.sort((a, b) => !a.days || +a.days < +b.days ? 1 : -1);
    this.items.find(f => f.id === 'paymentPlanText').data = this.paymentPlanText?.sort((a, b) => a.text.localeCompare(b.text));
    return super.saveItems().then(() => {
      this.store.reloadCompilationData().then(() => this.initItems(this.getItems()));
    })
  }

  addRow(section: string, idx: number) {
    this[section].splice(idx+1, 0, {});
  }
  removeRow(section: string, idx: number) {
    this[section].splice(idx, 1);
  }

  getSectionName(section: string) {
    console.log('items...', this.items);
    return this.items.find(f => f.id === section)?.name;
  }

}

export class PackingUtil extends AbstractEditor {
  protected title = 'Packing Details Editor';
  protected items: any = [
    { id: 'palletDetails', name: 'Pallet details', data: [] },
    { id: 'boxDetails', name: 'Box details', data: [] },
    // { id: 'boxLayer', name: 'Box / layer', data: [{}] },
    // { id: 'multiplier', name: 'Multiplier', data: 2 },
    { id: 'finalPacking', name: 'Final packing definition', data: [] },
  ]

  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, public store: SharedDataService, private component: UtilitiesComponent) {
    super(utilType, api, toast);
  }

  reportDateFieldOptions = DocConfig.reportDateFieldOptions;

  // reportDateFieldOptions = [
  //   { id: 'issueDate', name: 'Issue Date' },
  //   ...DocConfig.getArray().filter(f => 
  //     f.value.type === 'reportDate' 
  //     && f.key !== 'maxArrivingVieDate'
  //   ).map(m => ({ id: m.key, name: m.value.label })),
  // ]

  palletDetails: any[];
  palletOptions = [];
  boxDetails: any[];
  boxOptions = [];
  // boxLayer: any[];
  // multiplier: any;
  finalPacking: any[];
  public overrideDefaultSizes() {
    PalletType.getOptions().forEach(el => {
      const pal = this.palletDetails?.find(f => f.id === el.id) || {};
      if (!pal.id) this.palletDetails?.push(pal);
      pal.id = el.id;
      if (['P0', 'P9'].includes(el.id)) {
        pal.name = el.name;
      } else {
        const [_, length, width] = el.name.match(/(\d+)x(\d+)/);
        pal.name = el.name;
        pal.length = length;
        pal.width = width;
      }
      // console.log('=>>>>>', pal, this.palletDetails);
    })
    TypeOfCartonBox.getOptions().forEach(el => {
      if (!el.id) return;
      const box = this.boxDetails?.find(f => f.id === el.id) || {};
      if (!box.id) this.boxDetails?.push(box);
      // console.log('TypeOfCartonBox', el.id, el.name)
      const [_, length, width, height] = el.name.match(/(\d+)x(\d+)x(\d+)/);
      box.id = el.id;
      box.name = el.name;
      box.length = length;
      box.width = width;
      box.height = height;
    })
  }

  public override initItems(items: any): void {
    this.palletDetails = this.items.find(f => f.id === 'palletDetails').data;
    this.boxDetails = this.items.find(f => f.id === 'boxDetails').data;
    // this.boxLayer = this.items.find(f => f.id === 'boxLayer').data;
    // this.multiplier = this.items.find(f => f.id === 'multiplier').data;
    // fill palletDetails / boxDetails with defaults
    this.finalPacking = this.items.find(f => f.id === 'finalPacking').data; /*.data.map((m, idx) => { if (idx) m.heightAdjustment = m.heightAdjustment || 5; return m });*/
    this.overrideDefaultSizes();
    if (!this.palletDetails?.length) this.addRow('palletDetails', -1);
    if (!this.boxDetails?.length) this.addRow('boxDetails', -1);
    if (!this.finalPacking?.length) this.finalPacking = [{
      id: 'FPD0',
      name: 'non palletized shipment',
      pallet: 'P0',
    }, /*{
      id: 'FPD2',
      name: 'ship with other DN',
      pallet: 'P9'
    }*/]
    this.getOptions();
  }

  public override saveItems(items?: any): Promise<void> {
    this.overrideDefaultSizes();
    this.items.find(f => f.id === 'palletDetails').data = this.palletDetails;
    this.items.find(f => f.id === 'boxDetails').data = this.boxDetails;
    // this.items.find(f => f.id === 'boxLayer').data = this.boxLayer;
    // this.items.find(f => f.id === 'multiplier').data = this.multiplier;
    this.items.find(f => f.id === 'finalPacking').data = this.finalPacking;

    // console.log('save items', items);
    return super.saveItems(this.items).then(() => {
      // this.store.reloadCompilationData().then(() => this.initItems(this.getItems()));
      this.store.reloadCompilationData()
      // this.getOptions();
    })
  }

  getNextId(section: string, row: any) {
    let abbr = 'FPD';
    if (section === 'palletDetails') abbr = 'P'
    if (section === 'boxDetails') abbr = 'CB'
    const num = this[section]?.map(m => m.id)?.reduce((a, c) => {
      return Math.max(a, +c?.replace(/[^\d]+/, ''))
    }, 0);
    row.id = `${abbr}${num+1}`;
  }

  currentlyAdded = [];
  addRow(section: string, row: any) {
    let idx = this[section].findIndex(f => f === row);
    const newRow = {} as any
    this.getNextId(section, newRow);
    if (Object.keys(this.component.filterField).length) {
      this.component.filterField = {};
      this.component.filterRows(this.finalPacking);
      return;
    }
    if (section === 'finalPacking') {
      newRow.temperatureSticker = false;
      newRow.thermoShield = false
      newRow.edgeProtection = false;
      newRow.relabelling = false;
      newRow.outsideLabel = false;
      newRow.name = this.generateFpdName(newRow);
      newRow.heightAdjustment = 5;
    }
    this[section].splice(idx+1, 0, newRow);
    this.component.filterRows(this[section]);
    this.currentlyAdded.push(newRow);
  }

  removeRow(section: string, row: any) {
    let idx = this[section].findIndex(f => f === row);
    // if (Object.keys(this.component.filterField).length) {
    //   this.component.filterField = {};
    //   this.component.filterRows(this.finalPacking);
    //   return;
    // }
    this[section].splice(idx, 1);
    this.component.filterRows(this[section]);
  }

  canRemove(section: string, row: any) {
    let idx = this[section].findIndex(f => f === row);
    // if (Object.keys(this.component.filterField).length) {
    //   this.component.filterField = {};
    //   this.component.filterRows(this.finalPacking);
    //   return;
    // }
    return this.currentlyAdded.includes(this[section][idx]);
  }

  getSectionName(section: string) {
    return this.items.find(f => f.id === section)?.name;
  }

  // getBoxLayer() {
  //   const copy = (this.boxLayer && [...this.boxLayer]) || [];
  //   this.boxLayer = [];
  //   this.palletDetails?.forEach(pallet => {
  //     this.boxDetails?.forEach(box => {
  //       const row = copy.find(f => f.pallet === pallet.name && f.cartonBox === box.name);
  //       this.boxLayer.push(row ? row : { pallet: pallet.name, cartonBox: box.name});
  //       // console.log('getBoxLayer', pallet, box, row, copy, this.boxLayer)
  //     })
  //   })
  //   return this.boxLayer;
  // }

  getPalletDims(pallet: string) {
    const pal = this.palletDetails?.find(f => f.name === pallet);
    return `(${pal.length}x${pal.width}x${pal.height})`
  }

  getBoxDims(carton: string) {
    const box = this.boxDetails?.find(f => f.name === carton);
    return `(${box.length}x${box.width}x${box.height})`
  }

  getOptions() {
    this.palletOptions = this.palletDetails?.map(pal => ({ id: pal.id, name: pal.name }));
    this.boxOptions = this.boxDetails?.map(box => ({ id: box.id, name: box.name }));
  }

  // generateName(row, width) {
  //   row.name = width 
  //     ? 
  //     `${row.id} (${row.length}x${row.width}x${row.height})`
  //     :
  //     `${row.id} (${row.length}x${row.width})`
    
  //   this.getOptions();
  // }

  generateFpdName(row) {
    const pal = this.palletDetails?.find(f => f.id === row.pallet);
    const box = this.boxDetails?.find(f => f.id === row.cartonBox);
    const palDimensions = pal?.length && pal?.width ? `${pal?.length}x${pal?.width}` : 'non palletized';
    row.name = `${row.pallet} (${palDimensions}) ${row.cartonBox} (${box?.length}x${box?.width}x${box?.height}) ${row.boxesPerLayer}B ${row.layersPerPallet}L`
    if (row.temperatureSticker) row.name += ` T`;
    if (row.thermoShield) row.name += ` TS`;
    if (row.edgeProtection) row.name += ` EP`;
    if (row.relabelling) row.name += ` RL`;
  }

}


export class PfspoAvailabilityUtil extends AbstractEditor {
  protected title = 'PFSPO Availability Editor';
  protected items = [];
  public table = {};

  private sort = { col: null, dir: 1, codes: [] }

  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, public shared: SharedDataService) {
    super(utilType, api, toast);
    this.init();
  }

  async init() {
    await this.shared.loadBusinesses();
    await this.shared.loadProducts();
    await this.api.refreshCache();
    const spos = (await this.api.getCache(CacheType.doctype, { type: '^(PF)?SPO' })).docs.sort((a, b) => a.code[0].localeCompare(b.code[0]));
    const cs = (await this.api.getCache(CacheType.doctype, { type: '^CS' })).docs;
    const spoOcMap = await this.api.getCache(CacheType.spoOcMap);
    this.data = { spos, spoOcMap, cs };
    // console.log('data', this.data);

    this.reloadTable();

  }

  public reloadTable() {
    for (const doc of this.data.spos) {
      const relatedCS = this.data.cs?.find(f => f.shipmentLineItems.find(sli => sli.code === doc.code));
      const finishedProducts = doc.lineItems.map(m => {
        const fp = this.shared?.finishedProducts?.find(f => f._id === mongoId(m.finishedProduct))
        return fp;
      });
      this.table[doc.code] = {
        code: doc.code,
        recipient: this.shared?.businesses?.find(f => f._id === mongoId(doc.recipient))?.name || '',
        customerReference: this.shared?.businesses?.find(f => f._id === mongoId(doc.customerReference))?.name || '',
        relatedCS: relatedCS?.code,
        exwJapanDate: (relatedCS ? relatedCS.exwJapanDate : doc.exwJapanDate)?.replace(/\T.*/, ''),
        arrivingVieDate: (relatedCS ? relatedCS.arrivingVieDate : doc.arrivingVieDate)?.replace(/\T.*/, ''),
        product: finishedProducts?.map(m => m?.calcName).join('\n'),
        quantity: doc.lineItems.reduce((a, c) => a + c.quantity, 0)
          + (doc.lineItems.length > 1 ? ' = ' + doc.lineItems.map(m => +m.quantity).join(' + ') : ''),
        availabilityStatus: ''+doc.transportRun.availabilityStatus,
      }
      this.sort.codes = Object.keys(this.table);
    }
  }

  public onSort(col) {
    if (col === this.sort.col) this.sort.dir = -this.sort.dir;
    this.sort.col = col;
    this.sort.codes = Object.values(this.table)
      .sort((a, b) => (a[col] || '').localeCompare((b[col] || '')) * this.sort.dir)
      .map(row => row['code']);
  }

  public async availabilityChanged(code: string, value: number) {
    const res = await this.api.loadCompilationByCode(code);
    res.transportRun.availabilityStatus = +value;
    // console.log('availability changed', value, res);
    await this.api.saveCompilation(res);
  }

}

export class ProjectStatusUtil extends AbstractEditor {
  protected title = 'Project Status Editor';
  protected items = [];
  public table = {};

  private sort = { col: null, dir: 1, codes: [] }

  constructor(utilType: string, public api: ApiService, protected toast: NbToastrService, public shared: SharedDataService) {
    super(utilType, api, toast);
    this.init();
  }

  async init() {
    await this.shared.loadBusinesses();
    await this.shared.loadProducts();
    await this.api.refreshCache();
    // const spos = (await this.api.getCache(CacheType.doctype, { type: '^(PF)?SPO' })).docs.sort((a, b) => a.code[0].localeCompare(b.code[0]));
    // const cs = (await this.api.getCache(CacheType.doctype, { type: '^CS' })).docs;
    // const spoOcMap = await this.api.getCache(CacheType.spoOcMap);
    // this.data = { spos, spoOcMap, cs };
    const ocs: CompilationModel[] = (await this.api.getCache(CacheType.doctype, { type: '^OCNRKA' })).docs.sort((a, b) => a.code[0].localeCompare(b.code[0]));
    this.data = { ocs };
    // console.log('data', this.data);

    this.reloadTable();

  }

  public reloadTable() {
    for (const doc of this.data.ocs as CompilationModel[]) {
      // const relatedCS = this.data.cs?.find(f => f.shipmentLineItems.find(sli => sli.code === doc.code));
      const finishedProducts = doc.lineItems.map(m => {
        const fp = this.shared?.finishedProducts?.find(f => f._id === mongoId(m.finishedProduct))
        return fp;
      });
      if (doc.code === 'OCNRKA250157')console.log('doc', doc.code, doc.projectCompleted);
      this.table[doc.code] = {
        code: doc.code,
        recipient: this.shared?.businesses?.find(f => f._id === mongoId(doc.recipient))?.name || '',
        customerReference: this.shared?.businesses?.find(f => f._id === mongoId(doc.customerReference))?.name || '',
        // relatedCS: relatedCS?.code,
        // exwJapanDate: (relatedCS ? relatedCS.exwJapanDate : doc.exwJapanDate)?.replace(/\T.*/, ''),
        // arrivingVieDate: (relatedCS ? relatedCS.arrivingVieDate : doc.arrivingVieDate)?.replace(/\T.*/, ''),
        dipatchDate: moment(doc.vieDispatchDate).format('YY-MM-DD'),
        etaDate: moment(doc.etaCustomerDate).format('YY-MM-DD'),
        product: finishedProducts?.map(m => m?.calcName).join('\n'),
        quantity: doc.lineItems.reduce((a, c) => a + c.quantity, 0)
          + (doc.lineItems.length > 1 ? ' = ' + doc.lineItems.map(m => +m.quantity).join(' + ') : ''),
        projectCompleted: doc.projectCompleted || false,
      }
      this.sort.codes = Object.keys(this.table);
    }
  }

  public onSort(col) {
    if (col === this.sort.col) this.sort.dir = -this.sort.dir;
    this.sort.col = col;
    this.sort.codes = Object.values(this.table)
      .sort((a, b) => (a[col] || '').localeCompare((b[col] || '')) * this.sort.dir)
      .map(row => row['code']);
  }

  public async projectStatusChanged(code: string, value: boolean) {
    const res = await this.api.loadCompilationByCode(code);
    res.projectCompleted = value;
    // console.log('availability changed', value, res);
    await this.api.saveCompilation(res);
  }

}

export class StaticTextsUtil extends AbstractEditor {
  protected title = 'Static Texts';
  protected items = [
    { id: 'pfspoInDisclaimer', name: 'PFSPO IN disclaimer', data: '' },
    { id: 'orMailText', name: 'OR Mail', data: '' },
    { id: 'ocMailHeader', name: 'OC Mail Header', data: '' },
    { id: 'ocNoSpoText', name: 'OC Mail No SPO Text', data: '' },
    { id: 'ocNonCompletedSpoText', name: 'OC Mail Non Completed SPO Text', data: '' },
    { id: 'ocCompletedSpoText', name: 'OC Mail Completed SPO Text', data: '' },
    { id: 'ocMailFooter', name: 'OC Mail Footer', data: '' },
    { id: 'dnMailHeader', name: 'DN Mail Header', data: '' },
    { id: 'dnMailFooter', name: 'DN Mail Footer', data: '' },
    { id: 'inMailText', name: 'IN Mail Text', data: '' },
    { id: 'spoMailHeader', name: '(PF)SPO Mail Header', data: '' },
    { id: 'spoMailFooter', name: '(PF)SPO Mail Footer', data: '' },
  ]
}

export class ReportsMailUtil extends AbstractEditor {
  protected title = 'Reports Email';
  protected items = [
    { id: 'maillist', name: 'List of recipients', data: '' },
    { id: 'sendtime', name: 'Time to send', data: '18:00' },
    { id: 'subject', name: 'Subject', data: '' },
    { id: 'mailtext', name: 'Mail text', data: '' },
    { id: 'weekdays', name: 'Day of Week', data: [{ friday: true }] },
    { id: 'weeklyReports', name: 'Reports', data: [[null, true, true, true, true, true, true, true, true, true, true, true, true ]] },
    { id: 'actionPointMailtext', name: 'Actionpoint Content', data: '' },
    { id: 'actionPointHeader', name: 'Actionpoint Header', data: '' },
    { id: 'actionPointData', name: 'Actionpoint Data', data: '' },
    { id: 'config', name: 'Configuration', data: '' },
    { id: 'docsAndStockValue', name: 'Docs and Stock Html', data: '' }
  ]

  protected emailInfo: IWeeklyEmailInfo;

  override initItems(items: any): void {
    super.initItems(items);
    // this.emailInfo = {
    //   lastWeekOR: {
    //     nrvi: 0,
    //     nrmm: 0,
    //     nrbm: 0,
    //     em: 0
    //   },
    //   lastWeekIN: {
    //     nrvi: 0,
    //     nrmm: 0,
    //     nrbm: 0,
    //     em: 0
    //   },
    //   currentStockLevelEUR: 0,
    //   currentStockLevelUSD: 0,
    // };

    this.api.getWeeklyEmailInfo().then(info => {
      this.emailInfo = info;
      // items.find(f => f.id === 'docsAndStockValue').data = this.getDocsAndStockHtml(info);
    });
  }

  // override saveItems(items?: any): Promise<void> {
  //   items.find(f => f.id === 'docsAndStockValue').data = this.docsAndStockInfo?.nativeElement.innerHTML;
  //   return this.saveItems(items);
  // }

//   getDocsAndStockHtml(info: IWeeklyEmailInfo) {
//     const eur = new Intl.NumberFormat('de', {
//       style: 'currency',
//       currency: 'EUR',
//     });

//     const usd = new Intl.NumberFormat('de', {
//       style: 'currency',
//       currency: 'USD',
//     });

//     const html = `
// <br>
// <h5>Last week IN in EUR</h5>
// <table style="margin-bottom:0.5rem">
//   <tr>
//     <th style="border:1px solid black;padding:0.5rem;background-color:#ddd">BU</th>
//     <th style="border:1px solid black;padding:0.5rem;background-color:#ddd">EUR</th>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">NRVI</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekIN.nrvi)}</td>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">NRMM</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekIN.nrmm)}</td>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">NRBM</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekIN.nrbm)}</td>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">Energy Materials</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekIN.em)}</td>
//   </tr>
// </table>

// <br>

// <h5>Last week OR in EUR</h5>
// <table style="margin-bottom:0.5rem">
//   <tr>
//     <th style="border:1px solid black;padding:0.5rem;background-color:#ddd">BU</th>
//     <th style="border:1px solid black;padding:0.5rem;background-color:#ddd">EUR</th>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">NRVI</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekOR.nrvi)}</td>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">NRMM</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekOR.nrmm)}</td>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">NRBM</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekOR.nrbm)}</td>
//   </tr>
//   <tr>
//     <td style="border:1px solid black;padding:0.5rem">Energy Materials</td>
//     <td style="border:1px solid black;padding:0.5rem;text-align:right">${eur.format(info.lastWeekOR.em)}</td>
//   </tr>
// </table>

// <br><br>

// Current stock level in EUR: ${eur.format(info.currentStockLevelEUR)}<br>
// Current stock level in USD: ${usd.format(info.currentStockLevelUSD)}
// `;
//   }
}

export class ReportsMailUtil2 extends AbstractEditor {
  protected title = 'Reports Email 2';
  protected items = [
    { id: 'maillist', name: 'List of recipients', data: '' },
    { id: 'sendtime', name: 'Time to send', data: '18:00' },
    { id: 'subject', name: 'Subject', data: '' },
    { id: 'mailtext', name: 'Mail text', data: '' },
    { id: 'weekdays', name: 'Day of Week', data: [{ friday: true }] },
    { id: 'weeklyReports', name: 'Reports', data: [[null, true]] },
    { id: 'actionPointMailtext', name: 'Actionpoint Content', data: '' },
    { id: 'actionPointHeader', name: 'Actionpoint Header', data: '' },
    { id: 'actionPointData', name: 'Actionpoint Data', data: '' },
    { id: 'config', name: 'Configuration', data: '' },
  ]
}

export class ReportsMailUtilHQ extends AbstractEditor {
  protected title = 'Reports Email HQ';
  protected items = [
    { id: 'maillist', name: 'List of recipients', data: '' },
    { id: 'sendtime', name: 'Time to send', data: '22:55' },
    { id: 'subject', name: 'Subject', data: '' },
    { id: 'mailtext', name: 'Mail text', data: '' },
    { id: 'weekdays', name: 'Day of Week', data: [{ friday: true }] },
    { id: 'weeklyReports', name: 'Reports', data: [[null, true,]] },
    // { id: 'actionPointMailtext', name: 'Actionpoint Content', data: '' },
    // { id: 'actionPointHeader', name: 'Actionpoint Header', data: '' },
    // { id: 'actionPointData', name: 'Actionpoint Data', data: '' },
    { id: 'config', name: 'Configuration', data: '' },
  ]
}

@Component({
  selector: 'ngx-utilities',
  templateUrl: './utilities.component.html',
  styleUrls: ['./utilities.component.scss'],
})
export class UtilitiesComponent implements OnInit, DoCheck {

  @ViewChild('fpdTable') fpdTable!: ElementRef;
  @ViewChild(QuillEditorComponent) quillEditor: QuillEditorComponent;

  utilType = 'checklist';
  editorUtil: AbstractEditor;
  // checklist: Checklist;
  editorItems = [];
  editorItem = '';

  // palletOptions = PalletType.getOptions();
  // cartonBoxOptions = TypeOfCartonBox.getOptions();

  compilationTypes = CompilationTypeData.getTypes();

  contactField = EContactFieldType;
  packingData = PackingStatusData;
  reportNames = ReportsComponent.getCWReportNamesForReportsMailer();
  reportNames2 = ReportsComponent.getCWReportNamesForReportsMailer2();
  reportNamesHQ = ReportsComponent.getCWReportNamesForReportsMailerHQ();

  filterField: { [id: string]: { search: any, method?: string } } = {};
  filteredRows = [] as any;

  quillModules =  {
    toolbar: [
      // ['bold', 'italic', 'underline'], // Formatting options
      // ['link', 'image'], // Link and image options
      // [{ list: 'ordered' }, { list: 'bullet' }], // Lists
      // [{ align: [] }], // Alignment
      // ['table-better']
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      ['blockquote', 'code-block'],

      [{ 'header': 1 }, { 'header': 2 }],               // custom button values
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],         // outdent/indent
      [{ 'direction': 'rtl' }],                         // text direction

      [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults
      [{ 'font': [] }],
      [{ 'align': [] }],

      ['link', 'image', 'video'], // remove 'image' if needed
      ['clean'],                         // remove formatting
      ['table-better']
    ],
    table: false,
    blotFormatter: {},
    keyboard: {
      bindings: QuillTableBetter.keyboardBindings
    },
    'table-better': {
      language: 'en_US',
      menus: [
        'column',
        'row',
        'merge',
        'table',
        'cell',
        'wrap',
        'copy',
        'delete',
      ],
      toolbarTable: true,
    },
  }

  constructor(protected api: ApiService, private route: ActivatedRoute, private cdRef: ChangeDetectorRef,
    private router: Router, private toast: NbToastrService, private shared: SharedDataService) {

    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) {
        // console.log('util navigation end');
        this.init();
      }
    })
  }

  // quillIsAvailable = false
  ngDoCheck(): void {

    // if (this.quillEditor && !this.quillIsAvailable) {
    //   // console.log('content', this.quillEditor, this.editorItems[3].data);
    //   const quill = this.quillEditor.quillEditor;
    //   quill.setContents(quill.clipboard.convert({html: this.editorItems[3].data}), 'silent')
    //   this.quillIsAvailable = true;
    // }

  }

  ngOnInit(): void {
    this.init();
        
    console.log('getCWReportNamesForReportsMailer', ReportsComponent.getCWReportNamesForReportsMailer());
  }

  // ngAfterViewInit() {
  //   if (this.quillEditor) {
  //     const quill = this.quillEditor.quillEditor;
  //     console.log('content', this.editorItems[3].data);
  //     quill.setContents(quill.clipboard.convert(this.editorItems[3].data));
  //   }
  // }

  async init() {
    this.utilType = this.route.snapshot.params.id;
    switch (this.utilType) {
      case 'checklist': this.editorUtil = new ChecklistUtil('checklist', this.api, this.toast); break;
      case 'changelog': this.editorUtil = new ChangelogUtil('changelog', this.api, this.toast); break;
      case 'fileDescriptions': this.editorUtil = new FileDescriptionUtil('fileDescriptions', this.api, this.toast); break;
      case 'taxHintStore': this.editorUtil = new TaxHintUtil('taxHintStore', this.api, this.toast); break;
      case 'displayDatesStore': this.editorUtil = new DisplayDatesTextUtil('displayDatesStore', this.api, this.toast); break;
      case 'currency': this.editorUtil = new CurrencyUtil('currency', this.api, this.toast, this.shared); break;
      case 'hsCode': this.editorUtil = new HsCodeUtil('hsCode', this.api, this.toast, this.shared); break;
      case 'fiscalYear': this.editorUtil = new FiscalYearUtil('fiscalYear', this.api, this.toast, this.shared); break;
      case 'legacyData': this.editorUtil = new LegacyDataUtil('legacyData', this.api, this.toast); break;
      case 'paymentStore': this.editorUtil = new PaymentTextUtil('paymentStore', this.api, this.toast, this.shared); break;
      case 'packingStore': this.editorUtil = new PackingUtil('packingStore', this.api, this.toast, this.shared, this); break;
      case 'businessToP': this.editorUtil = new BusinessToPUtil('businessToP', this.api, this.toast, this.shared); break;
      case 'pfspoAvailability': this.editorUtil = new PfspoAvailabilityUtil('pfspoAvailability', this.api, this.toast, this.shared); break;
      case 'projectStatus': this.editorUtil = new ProjectStatusUtil('projectStatus', this.api, this.toast, this.shared); break;
      case 'staticTexts': this.editorUtil = new StaticTextsUtil('staticTexts', this.api, this.toast); break;
      case 'reportsMail': this.editorUtil = new ReportsMailUtil('reportsMail', this.api, this.toast); break;
      case 'reportsMail2': this.editorUtil = new ReportsMailUtil2('reportsMail2', this.api, this.toast); break;
      case 'reportsMailHQ': this.editorUtil = new ReportsMailUtilHQ('reportsMailHQ', this.api, this.toast); break;
    }
    // console.log('editor util', this.editorUtil);
    this.editorItems = await this.editorUtil.getItems();
    // console.log('tax hint', this.editorItems)

    switch (this.utilType) {
      case 'checklist':
      case 'changelog':
      case 'fileDescriptions':
        this.editorItems.filter(notNull => notNull).forEach(el => el.data = el.data.join('\n'));
        break;
      case 'displayDatesStore':
      case 'taxHintStore':
        this.editorItem = this.editorItems.filter(notNull => notNull).map(m => (typeof m === 'string' && m || '').replace(/\n/g, '↵')).join('\n');
        break;
      case 'packingStore':
        this.filterRows((this.editorUtil as PackingUtil).finalPacking);
        break;
      // case 'paymentStore': this.editorUtil = new PaymentTextUtil('paymentStore', this.api, this.toast);
      //   this.editorItem = this.editorItems.join('\n');
      //   break;
      case 'reportsMail':
        // this is a preview; value is set in backend
        if (this.editorItems?.[2]) this.editorItems[2].data = `${moment().format('YYMMDD')} ${moment().format('YY')}CW${moment().format('WW')} Overview NR Europe`
        if (!this.editorItems[3]?.data) this.editorItems[3].data = '';
        if (!this.editorItems[9]?.data) this.editorItems[9].data = [{ sendActionpoints: true }];
        console.log('this.editorItems[3]', this.editorItems[3].data, this.quillRef)
        // window.setTimeout(() => {
        //   const quill = this.quillEditor.quillEditor;
        //   quill.setContents(quill.clipboard.convert({html: this.editorItems[3].data}), 'silent')
        // }, 500);
        if (this.quillRef) this.onEditorCreated(this.quillRef);
        break;
      case 'reportsMail2':
        // this is a preview; value is set in backend
        if (this.editorItems?.[2]) this.editorItems[2].data = `${moment().format('YYMMDD')} Activity Overview NRK Europe`
        if (!this.editorItems[3]?.data) this.editorItems[3].data = '';
        if (!this.editorItems[9]?.data) this.editorItems[9].data = [{ sendActionpoints: true }];
        console.log('this.editorItems[3]', this.editorItems[3].data, this.quillRef)
        // window.setTimeout(() => {
        //   const quill2 = this.quillEditor.quillEditor;
        //   quill2.setContents(quill2.clipboard.convert({html: this.editorItems[3].data}), 'silent')
        // }, 500);
        if (this.quillRef) this.onEditorCreated(this.quillRef);
        break;
      case 'reportsMailHQ':
        // this is a preview; value is set in backend
        if (this.editorItems?.[2]) this.editorItems[2].data = `${moment().format('YYMMDD')} Monthly Report`
        if (!this.editorItems[3]?.data) this.editorItems[3].data = '';
        // if (!this.editorItems[9]?.data) this.editorItems[9].data = [{ sendActionpoints: false }];
        console.log('this.editorItems[3]', this.editorItems[3].data, this.quillRef)
    }
    // window.setTimeout(() => {
    //   this.cdRef.detectChanges();
    // }, 5000);
  }


  quillRef = undefined;
  async onEditorCreated(quill: Quill) {
    this.quillRef = quill;
    // await new Promise((res) => {
    //   let timeout = 1000
    //   const timer = setInterval(() => {
    //     console.log(timeout < 0 || this.editorItems[3]);
    //     if (timeout < 0 || this.editorItems[3]?.data) {
    //       clearInterval(timer);
    //       res(true);
    //     }
    //     timeout -= 10;
    //   }, 10)
    // });
    // if (!this.editorItems[3]?.data) return;
    // quill.setContents(quill.clipboard.convert({html: this.editorItems[3].data}), 'silent')
    const delta = quill.clipboard.convert({ html: this.editorItems[3].data });
    quill.setText('');
    const [range] = quill.selection.getRange();
    quill.updateContents(delta, Quill.sources.USER);
    quill.setSelection(
      delta.length() - (range?.length || 0),
      Quill.sources.SILENT
    );
    // quill.scrollSelectionIntoView();
    this.cdRef.markForCheck();
  }

  // private popupWindow: Window;
  openActionPointWin(report = 'reportsMail') {
    const url = `${window.location.origin}/#/popup/mail?dataRef=${report}`;
    window.open(
      url,
      '_blank',
      'width=1024,height=600,toolbar=no,location=no,menubar=no,resizable=yes,scrollbars=yes'
    );
    (window as any).onPopupData = (data: any) => {
      // TB#2838
      // this.editorItems[6] = data[6]
      // this.editorItems[7] = data[7]
      // this.editorItems[8] = data[8]
      console.log('editor items', data, this.editorItems);
    };
  }


  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.closePopup();
  }

  ngOnDestroy() {
    this.closePopup();
  }

  private closePopup() {
    // if (this.popupWindow && !this.popupWindow.closed) {
    //   this.popupWindow.postMessage({ type: 'close' }, '*');
    // }
  }

  async save() {

    // console.log('saving utility');
    let items = [];
    switch (this.utilType) {
      case 'checklist':
      case 'changelog':
      case 'fileDescriptions':
        items = JSON.parse(JSON.stringify(this.editorItems));
        items.forEach(el => el.data = el.data.split('\n'));
        await this.editorUtil.saveItems(items);
        return;

      case 'displayDatesStore':
      case 'taxHintStore':
        items = this.editorItem.split('\n').map(m => m.replace(/↵/g, '\n'));
        await this.editorUtil.saveItems(items);
        return;

      case 'currency':
        let valid = true;
        this.editorUtil.getData().forEach(row => {
          this.editorUtil.getColumns().forEach(col => {
            if (col.type === 'number' && (isNaN(row[col.id]) || !row[col.id])) valid = false;
          })
        });
        if (!valid) {
          this.toast.danger('Currency value is not a number', 'Error');
          return;
        }
        this.editorUtil.setData(this.editorUtil.getData().sort((a, b) => new Date(a.validFrom) < new Date(b.validFrom) ? 1 : -1));
        break;

      case 'hsCode':
        let valid2 = true;
        this.editorUtil.getData().forEach(row => { if (!row['code']) valid2 = false; row['type'] = +row['type']; });
        if (!valid2) {
          this.toast.danger('HS Code is empty', 'Error');
          return;
        }
        break;

      case 'legacyData':
        if (this.customerNames) {
          this.loadingLegacyData = true;
          for (const name of this.customerNames) {
            if (this.businessReference[name]) {
              for (const doc of this.docsLegacyData) {
                if (doc.customerName === name) {
                  await this.api.updateField(doc._id, 'customerReference', this.businessReference[name], true);
                }
              }
            }
          }
          this.loadingLegacyData = false;
          this.analyzeLegacyData();
        }
        return;
      case 'paymentStore':
      case 'packingStore':
      case 'taxHintStore':
      case 'staticTexts':
      case 'reportsMail':
      case 'reportsMail2':
      case 'reportsMailHQ':
        console.log('save', this.editorItems);
        if (this.utilType === 'reportsMail' || this.utilType === 'reportsMail2') {
          await this.shared.reloadCompilationData();
          const actionPointData = this.shared.compilationData[this.utilType][6];
          this.editorItems[6] = actionPointData;
        }
        await this.editorUtil.saveItems(this.editorItems);
        if (['reportsMail', 'reportsMail2', 'reportsMailHQ'].includes(this.utilType)) this.api.restartScheduler();
        return;
    }
    await this.editorUtil.saveItems();
  }

  filterRows(data) {
    console.log('ff',this.filterField, (this.editorUtil as any).finalPacking, data);
    // Object.filterthis.filterField
    // this.filteredRows = data?.filter(f => !this.filterField.id || !this.filterField.search || new RegExp(this.filterField.search).test(f[this.filterField.id]));
    // this.filteredRows = Object.keys(this.filterField).map(key => 
    //   data?.reduce((a, c) => a && (!key || !this.filterField[key].search || new RegExp(this.filterField[key].search).test(c[key]), true)
    // ))
    this.filteredRows = data?.filter(row => 
      Object.keys(this.filterField).reduce((bool, key) => 
        bool 
        && (
          !key 
          || !this.filterField[key].search 
          || new RegExp(this.filterField[key].search?.toLowerCase()
        ).test(row[key]?.toLowerCase())
      ), true)
    )
    console.log(data, this.filterField, this.filteredRows);
  }

  addCurrencyRow = (idx: number) => (this.editorUtil as CurrencyUtil).addRow(idx);
  removeCurrencyRow = (idx: number) => (this.editorUtil as CurrencyUtil).removeRow(idx);

  addHsCodeRow = (idx: number) => (this.editorUtil as HsCodeUtil).addRow(idx);
  removeHsCodeRow = (idx: number) => (this.editorUtil as HsCodeUtil).removeRow(idx);

  // moveUp(array: any[], idx: number) {
  //   console.log('moveUp', `>>${idx}<<`, array);
  //   if (idx === 0) return;
  //   const element = array.splice(idx, 1)[0];
  //   array.splice(idx - 1, 0, element);
  // }

  // moveDown(array: any[], idx: number) {
  //   console.log('moveDown', `>>${idx}<<`, array);
  //   if (idx >= array.length - 1) return;
  //   const element = array.splice(idx, 1)[0];
  //   array.splice(idx + 1, 0, element);  
  // }

  moveUp<T>(array: T[], index: number): void {
    if (index <= 0 || index >= array.length) return;
    [array[index - 1], array[index]] = [array[index], array[index - 1]];
  }
  moveDown<T>(array: T[], index: number): void {
    if (index < 0 || index >= array.length - 1) return;
    [array[index], array[index + 1]] = [array[index + 1], array[index]];
  }

  // ============================================= legacy data editor ===================================================

  businessReference: any = {};
  savedReference: any = {};
  businessOptions: any[];
  customerNames: string[];
  docsLegacyData: CompilationModel[];
  loadingLegacyData = false;

  analyzeLegacyData() {
    this.loadingLegacyData = true;
    this.api.reloadBusiness().subscribe(async businesses => {
      this.loadingLegacyData = false;
      this.businessOptions = businesses.map(m => ({ id: m._id, name: m.name })).sort((a, b) => a.name > b.name ? 1 : -1);
      const docs = await this.api.filterCompilations({}, '_id code version customerReference customerName', false);
      this.customerNames = docs && docs
        // .filter(f => !f.customerReference)
        .map(m => m.customerName)
        .filter((v, i, s) => v && s.indexOf(v) === i)
        .sort((a, b) => a > b ? 1 : -1)

      this.customerNames.forEach(el => {
        this.businessReference[el] = docs.find(f => el && f.customerName && el === f.customerName).customerReference;
      })
      this.savedReference = Object.assign({}, this.businessReference);

      this.docsLegacyData = docs;

    })
  }

  testDropdown(row, event) {
    console.log('testDropdown', row, event, (this.editorUtil as any).palletOptions, (this.editorUtil as any).boxOptions)
  }

  async analyzeDeliveryNotes() {

    this.loadingLegacyData = true;

    this.editorUtil.deliveryNotes = (await this.api.filterCompilations(
      { type: CompilationTypeData.get('DN').id, }, 
      '_id code parent documentStatus vieDispatchDate packingStatus proveOfDelivery',
      true)
    ).filter(dn => +dn.documentStatus === DocumentStatusData.get('final').id); // TODO: clean up database => legacy string values for documentStatus

    this.editorUtil.packingLists = (await this.api.filterCompilations(
      { type: CompilationTypeData.get('PL').id },
      '_id code parent documentStatus vieDispatchDate',
      true)
    ).filter(pl => +pl.documentStatus === DocumentStatusData.get('final').id)

    this.editorUtil.orderConfirmations = (await this.api.filterCompilations(
      { type: CompilationTypeData.get('OC').id },
      '_id code parent documentStatus vieDispatchDate',
      true)
    ).filter(oc => +oc.documentStatus === DocumentStatusData.get('final').id)

    this.editorUtil.invoices = (await this.api.filterCompilations(
      { type: { $in: [CompilationTypeData.get('IN').id, CompilationTypeData.get('PFIN').id] } },
      '_id code parent documentStatus proveOfDelivery',
      true)
    ).filter(inv => +inv.documentStatus === DocumentStatusData.get('final').id)

    console.log('docs', this.editorUtil.deliveryNotes, this.editorUtil.packingLists, this.editorUtil.orderConfirmations, this.editorUtil.invoices);

    this.editorUtil.deliveryNotes.forEach(dn => {

      this.editorUtil.dnTree[dn.code] = {} as any;

      this.editorUtil.dnTree[dn.code].dn = dn;

      this.editorUtil.dnTree[dn.code].in = this.editorUtil.invoices.find(inv => inv.parent === dn.code);
      (dn as any).invoicePoD = this.editorUtil.dnTree[dn.code].in?.proveOfDelivery;

      const pl = this.editorUtil.packingLists.find(pl => pl.code === dn.parent)
      this.editorUtil.dnTree[dn.code].pl = pl;
      if (pl) {
        this.editorUtil.dnTree[dn.code].oc = this.editorUtil.orderConfirmations.find(oc => oc.code === pl.parent);
        (dn as any).ocDispatch = this.editorUtil.dnTree[dn.code].oc?.vieDispatchDate;
        // console.log('packing list', pl, this.editorUtil.orderConfirmations.find(oc => oc.code === pl.parent));
        
      } else {
        this.editorUtil.dnTree[dn.code].oc = this.editorUtil.orderConfirmations.find(oc => oc.code === dn.parent);
        (dn as any).ocDispatch = this.editorUtil.dnTree[dn.code].oc?.vieDispatchDate;
        console.log('order confirmation', this.editorUtil.orderConfirmations.find(oc => oc.code === dn.parent));
      }
    });


    this.loadingLegacyData = false;

    (this.editorUtil as LegacyDataUtil).deliveryNotes = this.editorUtil.deliveryNotes.sort((a: any, b: any) => {
      if (!a.ocDispatch && !b.ocDispatch) return 0 as any;
      if (!a.ocDispatch) return 1;
      if (!b.ocDispatch) return -1;
      return new Date(b.ocDispatch).getTime() - new Date(a.ocDispatch).getTime();
    });
  }

  async exportToExcel(table: HTMLTableElement) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');
  
    // Get headers with colspan handling
    const headerRow: (string | null)[] = [];
    const thElements = Array.from(table.querySelectorAll('th')).slice(4, -2);
  
    thElements.forEach(th => {
      const colspan = th.getAttribute('colspan');
      const colSpanCount = colspan ? parseInt(colspan, 10) : 1;
      const headerText = th.textContent;
  
      // Add header text and empty values for colspan
      headerRow.push(headerText);
      for (let i = 1; i < colSpanCount; i++) {
        headerRow.push(''); // Fill in blank cells for the colspan
      }
    });
  
    worksheet.addRow(headerRow);
  
    // Get rows with colspan handling
    const rows = Array.from(table.querySelectorAll('tr'));
    rows.forEach(_row => {
      const row = Array.from(_row.querySelectorAll('td'));
      let cells: (string | number)[] = [];
  
      row.slice(5, row.length - 1).forEach(cell => {
        const colspan = cell.getAttribute('colspan');
        const colSpanCount = colspan ? parseInt(colspan, 10) : 1; // Default colspan to 1 if not set
  
        const input = cell.querySelector('input, select') as any;
        let cellValue = '';
  
        if (input) {
          if (input.tagName === 'INPUT') {
            if (input.type === 'checkbox') {
              cellValue = input.checked ? 'Yes' : ''; // Get the checked state for checkboxes
            } else {
              cellValue = input.value; // Get the value for text inputs
            }
          } else if (input.tagName === 'SELECT') {
            cellValue = input.value; // Get the selected value for select inputs
          }
        }
  
        // Add cell value and fill in additional cells for colspan if applicable
        cells.push(cellValue);
        for (let i = 1; i < colSpanCount; i++) {
          cells.push(''); // Fill in blank cells for the colspan
        }
      });
  
      worksheet.addRow(cells);
    });
  
    // Create a buffer and save
    const buffer = await workbook.xlsx.writeBuffer();
    this.saveAsExcelFile(buffer, 'data.xlsx');
  }

  saveAsExcelFile(buffer: any, filename: string) {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });
    saveAs(blob, filename);
  }

}
